<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Analitik"
      title="Journey Yönetimi"
      description="Mobil kullanıcı journey tanımlarını oluşturun, sıralı adımları düzenleyin ve basit funnel raporunu izleyin."
    >
      <template #actions>
        <div class="d-flex align-center flex-wrap">
          <v-btn
            v-if="journeyView === 1"
            outlined
            color="primary"
            small
            class="mr-2"
            :loading="seedLoading"
            @click="seedDialog = true"
          >
            Örnek Journeyleri Yükle
          </v-btn>
          <v-btn
            outlined
            color="primary"
            small
            class="mr-2"
            :loading="loading"
            @click="fetchJourneys"
          >
            Yenile
          </v-btn>
          <v-btn
            color="primary"
            small
            @click="openCreateDialog"
          >
            Yeni Journey
          </v-btn>
        </div>
      </template>
    </admin-page-header>

    <v-alert
      v-if="errorMessage"
      dense
      outlined
      type="error"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <v-alert
      v-if="successMessage"
      dense
      outlined
      type="success"
      class="mb-4"
    >
      {{ successMessage }}
    </v-alert>

    <v-tabs
      v-model="journeyView"
      class="mb-4"
    >
      <v-tab>Rapor</v-tab><v-tab>Tanımlar</v-tab>
    </v-tabs>
    <v-autocomplete
      v-if="journeyView === 0"
      :value="selectedJourney && selectedJourney.id"
      :items="journeys"
      item-text="name"
      item-value="id"
      label="Journey"
      outlined
      dense
      :loading="loading"
      @change="selectJourneyId"
    />
    <v-dialog
      v-model="seedDialog"
      max-width="520"
      persistent
    >
      <admin-confirm-dialog
        title="Örnek journeyleri ekle"
        description="Eksik varsayılan journey tanımları oluşturulacak. Mevcut tanımlar korunacak."
        confirm-text="Eksikleri Ekle"
        :save="seedDefaultJourneys"
        :close="() => { seedDialog = false }"
      />
    </v-dialog>
    <v-card
      v-show="journeyView === 1"
      class="admin-table-card analytics-journey-list-card mb-6"
      outlined
    >
      <v-toolbar
        dense
        flat
        class="admin-page-toolbar"
      >
        <v-toolbar-title>Journey listesi</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip
          small
          outlined
          color="primary"
        >
          {{ journeys.length }} kayıt
        </v-chip>
      </v-toolbar>

      <v-data-table
        :headers="journeyHeaders"
        :items="journeys"
        :loading="loading"
        :footer-props="tableFooterProps"
        :item-class="journeyRowClass"
        class="admin-data-table analytics-journey-table elevation-0"
        @click:row="selectJourney"
      >
        <template v-slot:item.name="{ item }">
          <div>
            <div class="font-weight-bold">
              {{ item.name || '-' }}
            </div>
            <div class="text-caption text--secondary">
              {{ item.description || '-' }}
            </div>
          </div>
        </template>

        <template v-slot:item.matchMode="{ item }">
          <v-chip
            x-small
            label
            :color="matchModeValue(item) === 'STRICT_ORDER' ? 'primary' : 'secondary'"
            outlined
          >
            {{ matchModeLabel(matchModeValue(item)) }}
          </v-chip>
        </template>

        <template v-slot:item.active="{ item }">
          <v-chip
            x-small
            label
            :color="statusValue(item) === 'ACTIVE' ? 'success' : 'grey'"
            :text-color="statusValue(item) === 'ACTIVE' ? 'white' : 'black'"
          >
            {{ statusValue(item) === 'ACTIVE' ? 'Aktif' : 'Pasif' }}
          </v-chip>
        </template>

        <template v-slot:item.steps="{ item }">
          {{ stepCount(item) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            icon
            small
            color="primary"
            aria-label="Journey raporunu aç"
            title="Raporu aç"
            @click.stop="selectJourney(item)"
          >
            <v-icon>{{ icons.mdiChartTimeline }}</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            color="primary"
            aria-label="Journey tanımını düzenle"
            title="Tanımı düzenle"
            @click.stop="openEditDialog(item)"
          >
            <v-icon>{{ icons.mdiPencilOutline }}</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            color="error"
            :loading="deletingId === item.id"
            aria-label="Journey tanımını sil"
            title="Tanımı sil"
            @click.stop="deleteJourney(item)"
          >
            <v-icon>{{ icons.mdiDeleteOutline }}</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-card
      v-show="journeyView === 0"
      class="admin-table-card analytics-report-card"
      outlined
    >
      <v-toolbar
        dense
        flat
        class="admin-page-toolbar"
      >
        <v-toolbar-title>Rapor</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          text
          small
          color="primary"
          :disabled="!selectedJourney"
          :loading="reportLoading"
          @click="fetchReport"
        >
          Raporu Yenile
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-alert
          v-if="!selectedJourney"
          text
          dense
          color="info"
          class="mb-0"
        >
          Funnel raporu için listeden bir journey seçin.
        </v-alert>

        <template v-else>
          <div class="d-flex align-center justify-space-between flex-wrap mb-4">
            <div>
              <div class="text-subtitle-1 font-weight-bold">
                {{ selectedJourney.name }}
              </div>
              <div class="text-caption text--secondary">
                {{ matchModeLabel(matchModeValue(selectedJourney)) }} eşleşme
              </div>
            </div>
            <v-chip
              small
              label
              :color="statusValue(selectedJourney) === 'ACTIVE' ? 'success' : 'grey'"
              :text-color="statusValue(selectedJourney) === 'ACTIVE' ? 'white' : 'black'"
            >
              {{ statusValue(selectedJourney) === 'ACTIVE' ? 'Aktif' : 'Pasif' }}
            </v-chip>
          </div>

          <v-row
            dense
            class="mb-4"
          >
            <v-col
              cols="12"
              sm="5"
            >
              <v-text-field
                v-model="reportFilters.from"
                outlined
                dense
                hide-details
                type="date"
                label="Başlangıç"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              sm="5"
            >
              <v-text-field
                v-model="reportFilters.to"
                outlined
                dense
                hide-details
                type="date"
                label="Bitiş"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              sm="2"
              class="d-flex align-center"
            >
              <v-btn
                block
                small
                color="primary"
                :loading="reportLoading"
                @click="fetchReport"
              >
                Uygula
              </v-btn>
            </v-col>
          </v-row>

          <v-alert
            v-if="reportError"
            dense
            outlined
            type="error"
            class="mb-4"
          >
            {{ reportError }}
          </v-alert>

          <v-row dense>
            <v-col
              v-for="item in reportCards"
              :key="item.label"
              cols="12"
              sm="4"
            >
              <v-card
                class="admin-dashboard-card"
                outlined
              >
                <v-card-text>
                  <div class="text-caption text--secondary">
                    {{ item.label }}
                  </div>
                  <div class="text-h6 font-weight-bold mt-1">
                    {{ item.value }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-card
            v-if="funnelRows.length"
            outlined
            class="analytics-funnel-card mt-4"
          >
            <v-card-title class="analytics-breakdown-title">
              Funnel görünümü
            </v-card-title>
            <v-card-text>
              <div
                v-for="item in funnelRows"
                :key="item.order"
                class="analytics-funnel-row"
              >
                <div class="analytics-funnel-row__meta">
                  <span class="analytics-funnel-row__order">{{ item.order }}</span>
                  <strong>{{ item.label }}</strong>
                  <small>{{ formatNumber(item.users) }} kullanıcı · {{ formatPercent(item.conversionRate) }}</small>
                </div>
                <div class="analytics-funnel-row__track">
                  <div
                    class="analytics-funnel-row__bar"
                    :style="{ width: `${item.width}%` }"
                  ></div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <v-data-table
            :headers="reportHeaders"
            :items="reportRows"
            :loading="reportLoading"
            hide-default-footer
            class="admin-data-table elevation-0 mt-4"
          >
            <template v-slot:item.conversionRate="{ item }">
              {{ formatPercent(item.conversionRate) }}
            </template>
          </v-data-table>

          <v-row
            v-if="breakdownSections.length"
            dense
            class="mt-4"
          >
            <v-col
              v-for="section in breakdownSections"
              :key="section.key"
              cols="12"
              md="4"
            >
              <v-card
                outlined
                class="analytics-breakdown-card"
              >
                <v-card-title class="analytics-breakdown-title">
                  {{ section.title }}
                </v-card-title>
                <v-data-table
                  :headers="breakdownHeaders"
                  :items="section.rows"
                  dense
                  hide-default-footer
                  class="admin-data-table elevation-0"
                >
                  <template v-slot:item.completionRate="{ item }">
                    {{ formatPercent(item.completionRate) }}
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>

          <v-card
            outlined
            class="analytics-path-card mt-4"
          >
            <v-card-title class="analytics-breakdown-title">
              Son kullanıcı pathleri
            </v-card-title>
            <v-expansion-panels
              v-if="userPathRows.length"
              accordion
              flat
            >
              <v-expansion-panel
                v-for="path in userPathRows"
                :key="path.sessionId"
              >
                <v-expansion-panel-header>
                  <div class="analytics-path-header">
                    <div>
                      <div class="font-weight-bold">
                        {{ shortSession(path.sessionId) }}
                      </div>
                      <div class="text-caption text--secondary">
                        User ID: {{ path.userId || '-' }} · {{ path.platform || '-' }}
                      </div>
                    </div>
                    <div class="analytics-path-meta">
                      <v-chip
                        x-small
                        label
                        :color="path.completed ? 'success' : 'grey'"
                        :text-color="path.completed ? 'white' : 'black'"
                      >
                        {{ pathStatusLabel(path) }}
                      </v-chip>
                      <span class="text-caption text--secondary">
                        {{ formatDateTime(path.startedAt) }}
                      </span>
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-data-table
                    :headers="pathEventHeaders"
                    :items="path.events || []"
                    dense
                    hide-default-footer
                    class="admin-data-table elevation-0"
                  >
                    <template v-slot:item.occurredAt="{ item }">
                      {{ formatDateTime(item.occurredAt) }}
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <v-card-text
              v-else
              class="text-caption text--secondary"
            >
              Seçili tarih aralığında path kaydı yok.
            </v-card-text>
          </v-card>
        </template>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="dialog"
      max-width="860"
      persistent
    >
      <v-card>
        <v-card-title class="d-flex align-center">
          <span>{{ editingId ? 'Journey Düzenle' : 'Yeni Journey' }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="closeDialog"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pt-6">
          <v-row dense>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model.trim="form.name"
                outlined
                dense
                label="Journey adı"
                :error-messages="formErrors.name"
              ></v-text-field>
            </v-col>

            <v-col
              cols="12"
              md="3"
            >
              <v-select
                v-model="form.matchMode"
                :items="matchModeOptions"
                outlined
                dense
                label="Match mode"
              ></v-select>
            </v-col>

            <v-col
              cols="12"
              md="3"
              class="d-flex align-center"
            >
              <v-switch
                v-model="form.active"
                inset
                dense
                hide-details
                label="Aktif"
                class="mt-0"
              ></v-switch>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model.trim="form.description"
                outlined
                dense
                rows="2"
                label="Açıklama"
              ></v-textarea>
            </v-col>
          </v-row>

          <div class="d-flex align-center justify-space-between mt-2 mb-3">
            <div>
              <div class="text-subtitle-2 font-weight-bold">
                Sıralı adımlar
              </div>
              <div class="text-caption text--secondary">
                Journey eşleşmesi bu sıradaki event/screen adımlarına göre hesaplanır.
              </div>
            </div>
            <v-btn
              outlined
              small
              color="primary"
              @click="addStep"
            >
              Adım Ekle
            </v-btn>
          </div>

          <v-alert
            v-if="formErrors.steps"
            dense
            outlined
            type="error"
          >
            {{ formErrors.steps }}
          </v-alert>

          <v-card
            v-for="(step, index) in form.steps"
            :key="step.localId"
            outlined
            class="mb-3"
          >
            <v-card-text class="py-4">
              <v-row
                dense
                align="center"
              >
                <v-col
                  cols="12"
                  md="1"
                >
                  <v-chip
                    small
                    label
                    color="primary"
                    outlined
                  >
                    {{ index + 1 }}
                  </v-chip>
                </v-col>

                <v-col
                  cols="12"
                  md="4"
                >
                  <v-select
                    v-model="step.stepType"
                    :items="eventTypeOptions"
                    outlined
                    dense
                    hide-details
                    label="Step tipi"
                  ></v-select>
                </v-col>

                <v-col
                  cols="12"
                  md="4"
                >
                  <v-combobox
                    v-model.trim="step.screenName"
                    :items="screenNameOptions"
                    outlined
                    dense
                    clearable
                    hide-details
                    label="Screen"
                    placeholder="revamp_offer_detail"
                  ></v-combobox>
                </v-col>

                <v-col
                  cols="12"
                  md="3"
                >
                  <v-combobox
                    v-model.trim="step.eventName"
                    :items="eventNameOptions"
                    outlined
                    dense
                    clearable
                    hide-details
                    label="Event name"
                    placeholder="link_create_success"
                  ></v-combobox>
                </v-col>

                <v-col
                  cols="12"
                  md="3"
                >
                  <v-text-field
                    v-model.trim="step.targetType"
                    outlined
                    dense
                    hide-details
                    label="Target type"
                    placeholder="campaign_link"
                  ></v-text-field>
                </v-col>

                <v-col
                  cols="12"
                  md="3"
                >
                  <v-text-field
                    v-model.trim="step.targetId"
                    outlined
                    dense
                    hide-details
                    label="Target ID"
                  ></v-text-field>
                </v-col>

                <v-col
                  cols="12"
                  md="2"
                >
                  <v-text-field
                    v-model.trim="step.stepKey"
                    outlined
                    dense
                    hide-details
                    label="Step key"
                    placeholder="success"
                  ></v-text-field>
                </v-col>

                <v-col
                  cols="12"
                  md="1"
                  class="d-flex justify-end"
                >
                  <v-btn
                    icon
                    small
                    :disabled="index === 0"
                    @click="moveStep(index, -1)"
                  >
                    <v-icon>{{ icons.mdiArrowUp }}</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="index === form.steps.length - 1"
                    @click="moveStep(index, 1)"
                  >
                    <v-icon>{{ icons.mdiArrowDown }}</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="error"
                    @click="removeStep(index)"
                  >
                    <v-icon>{{ icons.mdiDeleteOutline }}</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeDialog"
          >
            Vazgeç
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            @click="saveJourney"
          >
            Kaydet
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deleteDialog"
      max-width="420"
    >
      <v-card>
        <v-card-title>Journey silinsin mi?</v-card-title>
        <v-card-text>
          {{ deleteTargetName }} kalıcı olarak silinecek.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="deleteDialog = false"
          >
            Vazgeç
          </v-btn>
          <v-btn
            color="error"
            :loading="deletingId === deleteTargetId"
            @click="confirmDeleteJourney"
          >
            Sil
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import * as icons from '@mdi/js'
import { getAccessToken } from '@/utils/panelSession'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog.vue'
import { resolveApiErrorMessage } from '@/utils/apiErrors'

const DEFAULT_SCREEN_NAMES = [
  'login',
  'signup',
  'revamp_explore',
  'revamp_offer_detail',
  'revamp_create_link',
  'revamp_links',
  'revamp_link_detail',
  'revamp_profile',
  'revamp_interests',
  'revamp_points_transactions',
  'revamp_wallet',
  'revamp_subscriptions',
  'revamp_notifications',
]

const DEFAULT_EVENT_NAMES = [
  'screen_view',
  'screen_detail_view',
  'offer_card_click',
  'campaign_card_click',
  'shopping_preview_open',
  'link_create_start',
  'link_create_success',
  'link_create_fail',
  'link_preview_open',
  'interest_apply_start',
  'interest_apply_success',
  'point_convert_start',
  'point_convert_success',
  'wallet_withdraw_start',
  'wallet_withdraw_success',
  'premium_purchase_confirm_open',
  'premium_purchase_store_start',
  'premium_purchase_store_success',
]

const emptyForm = () => ({
  name: '',
  description: '',
  matchMode: 'STRICT_ORDER',
  active: true,
  steps: [],
})

export default {
  components: {
    AdminPageHeader,
    AdminConfirmDialog,
  },
  data: () => ({
    icons,
    loading: false,
    saving: false,
    seedLoading: false,
    seedDialog: false,
    journeyView: 0,
    reportSequence: 0,
    reportLoading: false,
    errorMessage: '',
    successMessage: '',
    reportError: '',
    deletingId: null,
    journeys: [],
    selectedJourney: null,
    report: {},
    analyticsMetadata: {
      screenNames: [],
      eventNames: [],
    },
    reportFilters: {
      from: '',
      to: '',
    },
    dialog: false,
    deleteDialog: false,
    deleteTarget: null,
    editingId: null,
    form: emptyForm(),
    formErrors: {},
    matchModeOptions: [
      { text: 'Kesin sıra', value: 'STRICT_ORDER' },
      { text: 'Esnek sıra', value: 'LOOSE_ORDER' },
    ],
    eventTypeOptions: [
      { text: 'Identify', value: 'IDENTIFY' },
      { text: 'Screen view', value: 'SCREEN_VIEW' },
      { text: 'CTA click', value: 'CTA_CLICK' },
      { text: 'Modal open', value: 'MODAL_OPEN' },
      { text: 'Modal close', value: 'MODAL_CLOSE' },
      { text: 'Form submit', value: 'FORM_SUBMIT' },
      { text: 'API success', value: 'API_SUCCESS' },
      { text: 'API fail', value: 'API_FAIL' },
      { text: 'Business success', value: 'BUSINESS_SUCCESS' },
      { text: 'Business fail', value: 'BUSINESS_FAIL' },
    ],
    journeyHeaders: [
      { text: 'Journey', value: 'name' },
      { text: 'Eşleşme', value: 'matchMode', width: 120 },
      { text: 'Adım', value: 'steps', width: 90 },
      { text: 'Durum', value: 'active', width: 110 },
      {
        text: 'İşlemler',
        value: 'actions',
        sortable: false,
        align: 'end',
        width: 150,
      },
    ],
    reportHeaders: [
      { text: '#', value: 'order', width: 60 },
      { text: 'Adım', value: 'label' },
      { text: 'Kullanıcı', value: 'users', width: 100 },
      { text: 'Oran', value: 'conversionRate', width: 100 },
    ],
    breakdownHeaders: [
      { text: 'Kırılım', value: 'label' },
      { text: 'Başlayan', value: 'startedSessions', width: 86 },
      { text: 'Tamamlayan', value: 'completedSessions', width: 96 },
      { text: 'Oran', value: 'completionRate', width: 80 },
    ],
    pathEventHeaders: [
      { text: 'Tarih', value: 'occurredAt', width: 150 },
      { text: 'Tip', value: 'eventType', width: 130 },
      { text: 'Event', value: 'eventName' },
      { text: 'Screen', value: 'screenName' },
      { text: 'Target', value: 'targetType', width: 110 },
    ],
  }),
  computed: {
    requestOptions() {
      return {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      }
    },
    tableFooterProps() {
      return {
        'items-per-page-options': [10, 20, 50, 100],
      }
    },
    reportRows() {
      const rows = this.report.steps || this.report.funnel || this.report.rows || []

      return rows.map((item, index) => ({
        order: item.order || item.stepOrder || index + 1,
        label: item.label || item.name || item.eventType || item.screenName || `Adım ${index + 1}`,
        users: item.users || item.userCount || item.count || item.reachedSessions || 0,
        conversionRate: item.conversionRate || item.rate || item.percent || 0,
      }))
    },
    reportCards() {
      const summary = this.report.summary || this.report || {}

      return [
        {
          label: 'Başlayan',
          value: this.formatNumber(summary.startedUsers || summary.started || summary.totalUsers || summary.totalSessions),
        },
        {
          label: 'Tamamlayan',
          value: this.formatNumber(summary.completedUsers || summary.completed || summary.convertedUsers || summary.completedSessions),
        },
        {
          label: 'Dönüşüm',
          value: this.formatPercent(summary.conversionRate || summary.rate),
        },
      ]
    },
    platformRows() {
      return this.normalizeBreakdown(this.report.platformBreakdown)
    },
    campaignRows() {
      return this.normalizeBreakdown(this.report.campaignBreakdown)
    },
    offerRows() {
      return this.normalizeBreakdown(this.report.offerBreakdown)
    },
    linkRows() {
      return this.normalizeBreakdown(this.report.linkBreakdown)
    },
    screenNameOptions() {
      return this.mergeOptions(this.analyticsMetadata.screenNames, DEFAULT_SCREEN_NAMES)
    },
    eventNameOptions() {
      return this.mergeOptions(this.analyticsMetadata.eventNames, DEFAULT_EVENT_NAMES)
    },
    funnelRows() {
      const rows = this.reportRows
      if (!rows.length) return []
      const maxUsers = Math.max(...rows.map(item => Number(item.users || 0)), 0)
      if (!maxUsers) return rows.map(item => ({ ...item, width: 0 }))

      return rows.map(item => ({
        ...item,
        width: Math.max(4, Math.round((Number(item.users || 0) / maxUsers) * 100)),
      }))
    },
    breakdownSections() {
      const sections = []
      if (this.platformRows.length) {
        sections.push({ key: 'platform', title: 'Platform', rows: this.platformRows })
      }
      if (this.journeyHasLinkDimension && this.linkRows.length) {
        sections.push({ key: 'link', title: 'Link', rows: this.linkRows })
      }
      if (this.journeyHasCampaignDimension && this.campaignRows.length) {
        sections.push({ key: 'campaign', title: 'Kampanya', rows: this.campaignRows })
      }
      if (this.journeyHasOfferDimension && this.offerRows.length) {
        sections.push({ key: 'offer', title: 'Offer', rows: this.offerRows })
      }

      return sections
    },
    journeyDimensionText() {
      const steps = this.normalizedSteps(this.selectedJourney || {})

      return steps
        .flatMap(step => [
          step.stepType,
          step.eventType,
          step.screenName,
          step.eventName,
          step.targetType,
          step.stepKey,
        ])
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
    },
    journeyHasLinkDimension() {
      return this.journeyDimensionText.includes('link')
    },
    journeyHasCampaignDimension() {
      return !this.journeyHasLinkDimension && this.journeyDimensionText.includes('campaign')
    },
    journeyHasOfferDimension() {
      const text = this.journeyDimensionText

      return text.includes('offer') || text.includes('brand') || text.includes('revamp_offer_detail')
    },
    userPathRows() {
      return Array.isArray(this.report.userPaths) ? this.report.userPaths : []
    },
    deleteTargetId() {
      return this.deleteTarget ? this.deleteTarget.id : null
    },
    deleteTargetName() {
      return this.deleteTarget?.name || 'Seçili journey'
    },
  },
  mounted() {
    this.fetchJourneys()
    this.fetchMetadata()
  },
  methods: {
    analyticsUrl(path) {
      return `${process.env.VUE_APP_BASE_API_URL}admin/analytics${path}`
    },
    async fetchMetadata() {
      try {
        const response = await axios.get(this.analyticsUrl('/metadata'), this.requestOptions)
        const payload = response?.data?.data || {}
        this.analyticsMetadata = {
          screenNames: payload.screenNames || [],
          eventNames: payload.eventNames || [],
        }
      } catch (error) {
        this.analyticsMetadata = {
          screenNames: [],
          eventNames: [],
        }
      }
    },
    async fetchJourneys() {
      this.loading = true
      this.errorMessage = ''

      try {
        const response = await axios.get(this.analyticsUrl('/journeys'), this.requestOptions)
        const payload = response?.data?.data || response?.data || {}
        this.journeys = payload.content || payload.items || payload.journeys || payload.records || payload || []

        if (this.selectedJourney) {
          const selected = this.journeys.find(item => item.id === this.selectedJourney.id)
          this.selectedJourney = selected || null
        }
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Journey listesi yüklenemedi.'
      } finally {
        this.loading = false
      }
    },
    async seedDefaultJourneys() {
      if (this.seedLoading) return false
      this.seedLoading = true
      this.errorMessage = ''
      this.successMessage = ''

      try {
        const response = await axios.post(this.analyticsUrl('/journeys/seed-defaults'), {}, this.requestOptions)
        const payload = response?.data?.data || {}
        const created = payload.created?.length || 0
        const skipped = payload.skipped?.length || 0
        this.successMessage = `Örnek journeyler hazırlandı. Oluşturulan: ${created}, mevcut: ${skipped}.`
        await this.fetchJourneys()

        return true
      } catch (error) {
        throw new Error(resolveApiErrorMessage(error, 'Örnek journeyler yüklenemedi.'))
      } finally {
        this.seedLoading = false
      }
    },
    async selectJourney(item) {
      this.selectedJourney = item
      this.journeyView = 0
      this.report = {}
      await this.fetchReport()
    },
    selectJourneyId(id) { this.selectJourney(this.journeys.find(item => item.id === id) || null) },
    async fetchReport() {
      this.reportSequence += 1
      if (!this.selectedJourney?.id) {
        this.reportLoading = false
        this.reportError = ''

        return
      }
      const sequence = this.reportSequence
      this.reportLoading = true
      this.reportError = ''

      try {
        const params = {}
        if (this.reportFilters.from) params.from = this.reportFilters.from
        if (this.reportFilters.to) params.to = this.reportFilters.to
        const response = await axios.get(this.analyticsUrl(`/journeys/${this.selectedJourney.id}/report`), {
          ...this.requestOptions,
          params,
        })
        if (sequence !== this.reportSequence) return
        this.report = response?.data?.data || response?.data || {}
      } catch (error) {
        if (sequence === this.reportSequence) this.reportError = resolveApiErrorMessage(error, 'Journey raporu yüklenemedi.')
      } finally {
        if (sequence === this.reportSequence) this.reportLoading = false
      }
    },
    openCreateDialog() {
      this.editingId = null
      this.form = emptyForm()
      this.form.steps = [this.createStep()]
      this.formErrors = {}
      this.dialog = true
    },
    openEditDialog(item) {
      this.editingId = item.id
      this.form = {
        name: item.name || '',
        description: item.description || '',
        matchMode: this.matchModeValue(item),
        active: this.statusValue(item) === 'ACTIVE',
        steps: this.normalizedSteps(item).map(step => this.createStep(step)),
      }
      if (!this.form.steps.length) {
        this.form.steps = [this.createStep()]
      }
      this.formErrors = {}
      this.dialog = true
    },
    closeDialog() {
      if (this.saving) return
      this.dialog = false
      this.formErrors = {}
    },
    addStep() {
      this.form.steps.push(this.createStep())
    },
    removeStep(index) {
      this.form.steps.splice(index, 1)
    },
    moveStep(index, direction) {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= this.form.steps.length) return
      const steps = [...this.form.steps]
      const selected = steps.splice(index, 1)[0]
      steps.splice(targetIndex, 0, selected)
      this.form.steps = steps
    },
    async saveJourney() {
      if (!this.validateForm()) return

      this.saving = true
      this.errorMessage = ''

      try {
        const payload = this.buildPayload()
        if (this.editingId) {
          await axios.put(this.analyticsUrl(`/journeys/${this.editingId}`), payload, this.requestOptions)
        } else {
          await axios.post(this.analyticsUrl('/journeys'), payload, this.requestOptions)
        }

        this.dialog = false
        await this.fetchJourneys()
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Journey kaydedilemedi.'
      } finally {
        this.saving = false
      }
    },
    deleteJourney(item) {
      this.deleteTarget = item
      this.deleteDialog = true
    },
    async confirmDeleteJourney() {
      if (!this.deleteTarget?.id || this.deletingId) return

      this.deletingId = this.deleteTarget.id
      this.errorMessage = ''

      try {
        await axios.delete(this.analyticsUrl(`/journeys/${this.deleteTarget.id}`), this.requestOptions)
        if (this.selectedJourney?.id === this.deleteTarget.id) {
          this.selectedJourney = null
          this.report = {}
        }
        this.deleteDialog = false
        this.deleteTarget = null
        await this.fetchJourneys()
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Journey silinemedi.'
      } finally {
        this.deletingId = null
      }
    },
    validateForm() {
      const errors = {}
      if (!this.form.name) {
        errors.name = 'Journey adı zorunlu.'
      }

      const validSteps = this.form.steps.filter(step => step.stepType || step.screenName || step.eventName)
      if (!validSteps.length) {
        errors.steps = 'En az bir step tipi, event name veya screen içeren adım ekleyin.'
      }

      this.formErrors = errors

      return Object.keys(errors).length === 0
    },
    buildPayload() {
      return {
        name: this.form.name,
        description: this.form.description,
        matchMode: this.form.matchMode,
        status: this.form.active ? 'ACTIVE' : 'PASSIVE',
        steps: this.form.steps
          .filter(step => step.stepType || step.screenName || step.eventName)
          .map((step, index) => ({
            id: step.id,
            stepOrder: index + 1,
            stepType: step.stepType || 'SCREEN_VIEW',
            screenName: step.screenName || null,
            eventName: step.eventName || null,
            targetType: step.targetType || null,
            targetId: step.targetId || null,
            stepKey: step.stepKey || null,
          })),
      }
    },
    createStep(step = {}) {
      return {
        localId: `${Date.now()}-${Math.random()}`,
        id: step.id,
        stepType: step.stepType || step.eventType || '',
        screenName: step.screenName || '',
        eventName: step.eventName || '',
        targetType: step.targetType || '',
        targetId: step.targetId || '',
        stepKey: step.stepKey || '',
      }
    },
    normalizedSteps(item) {
      const steps = item.steps || item.orderedSteps || []

      return [...steps].sort((a, b) => Number(a.order || a.stepOrder || 0) - Number(b.order || b.stepOrder || 0))
    },
    stepCount(item) {
      return this.normalizedSteps(item).length
    },
    matchModeValue(item) {
      if (item.matchMode === 'STRICT') return 'STRICT_ORDER'
      if (item.matchMode === 'LOOSE') return 'LOOSE_ORDER'
      if (item.matchMode) return item.matchMode
      if (item.strictMatch === false) return 'LOOSE_ORDER'

      return 'STRICT_ORDER'
    },
    matchModeLabel(value) {
      return value === 'LOOSE_ORDER' ? 'Esnek sıra' : 'Kesin sıra'
    },
    statusValue(item) {
      if (item.status) return item.status

      return item.active === false ? 'PASSIVE' : 'ACTIVE'
    },
    formatNumber(value) {
      return new Intl.NumberFormat('tr-TR').format(Number(value || 0))
    },
    formatPercent(value) {
      const numeric = Number(value || 0)
      const normalized = numeric > 1 ? numeric : numeric * 100

      return `%${new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(normalized)}`
    },
    normalizeBreakdown(rows) {
      if (!Array.isArray(rows)) return []

      return rows.map(item => ({
        key: item.key || item.label || 'unknown',
        label: item.label || item.key || 'Bilinmiyor',
        startedSessions: item.startedSessions || item.started || 0,
        completedSessions: item.completedSessions || item.completed || 0,
        completionRate: item.completionRate || item.rate || 0,
      }))
    },
    mergeOptions(primary = [], fallback = []) {
      return [...new Set([...(primary || []), ...(fallback || [])]
        .map(item => String(item || '').trim())
        .filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, 'tr'))
    },
    journeyRowClass(item) {
      return this.selectedJourney?.id === item.id
        ? 'analytics-journey-row analytics-journey-row--active'
        : 'analytics-journey-row'
    },
    shortSession(value) {
      const raw = String(value || '')
      if (raw.length <= 18) return raw || '-'

      return `${raw.slice(0, 10)}...${raw.slice(-6)}`
    },
    pathStatusLabel(path) {
      if (path?.completed) return 'Tamamlandı'

      return `${path?.reachedSteps || 0} adım`
    },
    formatDateTime(value) {
      if (!value) return '-'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return '-'

      return new Intl.DateTimeFormat('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(parsed)
    },
  },
}
</script>

<style lang="scss" scoped>
.analytics-journey-list-card,
.analytics-report-card {
  overflow: hidden;
}

.analytics-breakdown-card,
.analytics-path-card,
.analytics-funnel-card {
  border-radius: 16px;
}

.analytics-breakdown-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 12px 14px 8px;
}

.analytics-path-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.analytics-path-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.analytics-funnel-row + .analytics-funnel-row {
  margin-top: 14px;
}

.analytics-funnel-row__meta {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  margin-bottom: 8px;
}

.analytics-funnel-row__meta strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.analytics-funnel-row__meta small {
  color: #8b8796;
  font-weight: 700;
}

.analytics-funnel-row__order {
  width: 28px;
  height: 28px;
  border: 1px solid rgba(210, 16, 39, 0.28);
  border-radius: 8px;
  color: #d21027;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}

.analytics-funnel-row__track {
  width: 100%;
  height: 12px;
  border-radius: 999px;
  background: #f2f3f7;
  overflow: hidden;
}

.analytics-funnel-row__bar {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #d21027, #f15b65);
  transition: width 0.2s ease;
}

@media (max-width: 960px) {
  .analytics-path-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .analytics-path-meta {
    justify-content: flex-start;
  }

  .analytics-funnel-row__meta {
    grid-template-columns: 34px minmax(0, 1fr);
  }

  .analytics-funnel-row__meta small {
    grid-column: 2;
  }
}
</style>

<style lang="scss">
.analytics-journey-table tbody tr.analytics-journey-row {
  cursor: pointer;
}

.analytics-journey-table tbody tr.analytics-journey-row:hover {
  background: rgba(210, 16, 39, 0.04) !important;
}

.analytics-journey-table tbody tr.analytics-journey-row--active {
  background: rgba(210, 16, 39, 0.08) !important;
}
</style>
